import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * TvLink — Profile tab when user is not signed in on the TV.
 *
 * Shows a big short code + QR the viewer scans on their phone to sign in.
 * Polls /device-link/status/{device_id} every 3s; once claimed, the backend
 * returns a pre-minted JWT that we drop into `AuthContext`'s token storage
 * and refresh the user profile.
 */
export default function TvLink() {
  const { loginWithToken } = useAuth();
  const [code, setCode] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [status, setStatus] = useState("pending");
  const [err, setErr] = useState("");

  useEffect(() => {
    let stopped = false;
    let timer = null;

    async function start() {
      try {
        const { data } = await api.post("/device-link/start", { device_type: "tv", device_name: navigator.userAgent?.slice(0, 60) || "Smart TV" });
        if (stopped) return;
        setCode(data.code);
        setDeviceId(data.device_id);
        poll(data.device_id);
      } catch (e) {
        setErr("Couldn't start pairing. Please reload this TV app.");
      }
    }

    async function poll(id) {
      try {
        const { data } = await api.get(`/device-link/status/${id}`);
        if (stopped) return;
        if (data.status === "claimed") {
          setStatus("claimed");
          if (data.token) {
            try { loginWithToken(data.token, data.user); } catch {}
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {}
      timer = setTimeout(() => poll(id), 3000);
    }

    start();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line
  }, []);

  const restart = () => { setCode(null); setStatus("pending"); setErr(""); window.location.reload(); };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const linkUrl = `${origin}/link${code ? `?code=${code}` : ""}`;
  const qrUrl = code
    ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=10&data=${encodeURIComponent(linkUrl)}`
    : null;

  return (
    <div style={{ padding: "var(--tv-safe-y) var(--tv-safe-x)" }} className="max-w-[88%]" data-testid="tv-link">
      <h2 className="font-display text-[2.8em] font-black mb-3">Sign in to Tap IN TV</h2>
      <p className="text-white/60 text-[1.25em] mb-[calc(var(--tv-gap)*1.4)] max-w-[70%]">
        On your phone or computer, go to{" "}
        <span className="text-tv-accent font-bold">{origin.replace(/^https?:\/\//, "")}/link</span>{" "}
        and enter the code below. Or scan the QR code with your camera.
      </p>

      {err && <div className="text-red-400 text-[1.25em] mb-6">{err}</div>}

      {status === "pending" && code && (
        <div className="grid md:grid-cols-[1fr_auto] gap-[calc(var(--tv-gap)*1.8)] items-center">
          <div>
            <div className="text-white/50 uppercase tracking-[0.25em] text-[0.9em] font-bold mb-4">Your code</div>
            <div
              data-testid="tv-link-code"
              className="font-display font-black leading-none tracking-[0.15em] text-tv-accent select-all"
              style={{ fontSize: "clamp(5rem, 12vw, 18rem)" }}
            >
              {code}
            </div>
            <div className="text-white/60 text-[1.1em] mt-6">Code expires in 15 minutes.</div>
          </div>
          {qrUrl && (
            <div className="bg-white rounded-3xl p-[1.4em]" data-testid="tv-link-qr">
              <img src={qrUrl} alt="Scan to sign in" className="block" style={{ width: "clamp(240px, 22vw, 560px)", height: "clamp(240px, 22vw, 560px)" }} />
            </div>
          )}
        </div>
      )}

      {status === "claimed" && (
        <div className="bg-emerald-500/20 border border-emerald-500/40 rounded-3xl p-[2em]" data-testid="tv-link-claimed">
          <div className="font-display text-[2.6em] font-black text-emerald-300 mb-2">You're in!</div>
          <div className="text-white/80 text-[1.25em]">This TV is now paired. Enjoy Tap IN TV.</div>
        </div>
      )}

      {status === "expired" && (
        <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-3xl p-[2em]">
          <div className="font-display text-[2em] font-black mb-4">Code expired</div>
          <button
            data-tv-focus="true"
            onClick={restart}
            className="tv-focusable bg-tv-accent text-black font-bold text-[1.1em] px-[1.8em] py-[0.9em] rounded-full"
          >
            Get a new code
          </button>
        </div>
      )}
    </div>
  );
}
